"use client"

import Image from "next/image"
import { useEffect, useMemo, useRef, useState } from "react"
import { PiPantsThin } from "react-icons/pi"
import PromoBadge from "@modules/common/components/promo-badge"
import { resolveCampaignBadge } from "@lib/util/campaign"
import {
  PRODUCT_GALLERY_DESKTOP_MAIN_IMAGE_SIZES,
  PRODUCT_GALLERY_MOBILE_IMAGE_SIZES,
  SQUARE_THUMB_IMAGE_SIZES,
} from "@lib/util/storefront-image-sizes"
import {
  PRODUCT_GALLERY_MAIN_IMAGE_QUALITY,
  PRODUCT_GALLERY_THUMB_IMAGE_QUALITY,
} from "@lib/util/storefront-image-quality"
import PlaceholderImage from "@modules/common/icons/placeholder-image"

type ImageGalleryProps = {
  images: { url?: string; key?: string; id?: string }[]
  product?: any
  variantImageUrl?: string | null
  virtualTryOnEnabled?: boolean
  onVirtualTryOn?: () => void
}

type MediaItem =
  | ({ type: "image" } & { url: string; key?: string; id?: string })
  | { type: "video"; url: string; poster?: string }

const ImageGallery = ({
  images,
  product,
  variantImageUrl,
  virtualTryOnEnabled,
  onVirtualTryOn,
}: ImageGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [failedMediaUrls, setFailedMediaUrls] = useState<Set<string>>(
    () => new Set()
  )
  const mobileGalleryRef = useRef<HTMLDivElement | null>(null)
  const showVirtualTryOn = useMemo(
    () => Boolean(virtualTryOnEnabled && typeof onVirtualTryOn === "function"),
    [virtualTryOnEnabled, onVirtualTryOn]
  )

  const sanitizedImages = useMemo<MediaItem[]>(
    () =>
      Array.isArray(images)
        ? images
            .filter(
              (image) => typeof image?.url === "string" && image.url.trim()
            )
            .map((image) => ({
              type: "image" as const,
              url: (image.url || "").trim(),
              key: image.key,
              id: image.id,
            }))
        : [],
    [images]
  )

  const normalizedVariantImageUrl = useMemo(() => {
    if (typeof variantImageUrl !== "string") return ""
    const trimmed = variantImageUrl.trim()
    return trimmed.length ? trimmed : ""
  }, [variantImageUrl])

  const imagesWithVariant = useMemo<MediaItem[]>(() => {
    if (!normalizedVariantImageUrl) {
      return sanitizedImages
    }

    const alreadyIncluded = sanitizedImages.some(
      (image) =>
        image.type === "image" && image.url === normalizedVariantImageUrl
    )
    if (alreadyIncluded) {
      return sanitizedImages
    }

    return [
      {
        type: "image" as const,
        url: normalizedVariantImageUrl,
        key: `variant-${normalizedVariantImageUrl}`,
      },
      ...sanitizedImages,
    ]
  }, [normalizedVariantImageUrl, sanitizedImages])

  const mediaItems = useMemo<MediaItem[]>(() => {
    const list = [...imagesWithVariant]
    const videoUrl =
      typeof product?.videoUrl === "string" ? product.videoUrl.trim() : ""

    if (videoUrl) {
      list.push({
        type: "video",
        url: videoUrl,
        poster: imagesWithVariant[0]?.url,
      })
    }

    return list
  }, [product?.videoUrl, imagesWithVariant])

  const isPreOrder = useMemo(
    () => Boolean(product?.preOrder),
    [product?.preOrder]
  )
  const isMadeToOrder = useMemo(
    () => Boolean(product?.madeToOrder),
    [product?.madeToOrder]
  )
  const isPersonalized = useMemo(
    () => Boolean(product?.personalization?.enabled),
    [product?.personalization?.enabled]
  )
  const campaignBadge = useMemo(() => resolveCampaignBadge(product), [product])
  const isSale = useMemo(() => product?.isSale === true, [product?.isSale])
  const hasCampaignBadge = Boolean(campaignBadge) && !isSale
  const hasDiscountBadge = useMemo(
    () =>
      !isSale && typeof product?.discount === "number" && product.discount > 0,
    [product?.discount, isSale]
  )

  const renderBadges = () => {
    if (
      !hasCampaignBadge &&
      !isPreOrder &&
      !isMadeToOrder &&
      !isPersonalized &&
      !hasDiscountBadge &&
      !isSale
    )
      return null
    return (
      <div className="absolute right-4 top-4 flex flex-col items-end gap-2">
        {hasCampaignBadge ? (
          <PromoBadge className="flex min-h-[38px] min-w-[112px] max-w-[180px] items-center justify-center px-3 text-center text-[12px] font-lealti uppercase tracking-wide">
            {campaignBadge}
          </PromoBadge>
        ) : (
          <>
            {isPersonalized && (
              <PromoBadge className="flex h-[42px] w-[120px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                PERSONALIZADO
              </PromoBadge>
            )}
            {isMadeToOrder && (
              <PromoBadge className="flex h-[42px] w-[100px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                A PEDIDO
              </PromoBadge>
            )}
            {isPreOrder && (
              <PromoBadge className="flex h-[42px] w-[95px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                PRE VENTA
              </PromoBadge>
            )}
            {isSale ? (
              <PromoBadge className="flex h-[62px] w-[62px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                SALE
              </PromoBadge>
            ) : hasDiscountBadge ? (
              <PromoBadge className="flex h-[62px] w-[62px] items-center justify-center text-[12px] font-lealti-light uppercase tracking-wide">
                {product.discount}% OFF
              </PromoBadge>
            ) : null}
          </>
        )}
      </div>
    )
  }

  useEffect(() => {
    setSelectedIndex((prev) => {
      if (mediaItems.length === 0) return 0
      return Math.min(prev, mediaItems.length - 1)
    })
  }, [mediaItems.length])

  useEffect(() => {
    if (!normalizedVariantImageUrl) {
      setSelectedIndex(0)
      return
    }
    const targetIndex = mediaItems.findIndex(
      (item) => item.type === "image" && item.url === normalizedVariantImageUrl
    )
    if (targetIndex >= 0) {
      setSelectedIndex(targetIndex)
    }
  }, [mediaItems, normalizedVariantImageUrl])

  useEffect(() => {
    const container = mobileGalleryRef.current
    if (!container || typeof window === "undefined") {
      return
    }

    let animationFrame: number | null = null

    const handleScroll = () => {
      if (window.innerWidth >= 768) {
        return
      }

      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
      }

      animationFrame = window.requestAnimationFrame(() => {
        const newIndex = Math.round(
          container.scrollLeft / container.clientWidth
        )
        const boundedIndex = Math.max(
          0,
          Math.min(mediaItems.length - 1, newIndex)
        )
        setSelectedIndex((prev) =>
          prev === boundedIndex ? prev : boundedIndex
        )
      })
    }

    container.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      container.removeEventListener("scroll", handleScroll)
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
      }
    }
  }, [mediaItems.length])

  useEffect(() => {
    const container = mobileGalleryRef.current
    if (!container || typeof window === "undefined") {
      return
    }

    if (window.innerWidth >= 768) {
      return
    }

    const targetScrollLeft = selectedIndex * container.clientWidth

    if (Math.abs(container.scrollLeft - targetScrollLeft) > 1) {
      container.scrollTo({ left: targetScrollLeft, behavior: "smooth" })
    }
  }, [selectedIndex])

  if (!mediaItems || mediaItems.length === 0) {
    return (
      <div className="w-full h-96 bg-gray-100 rounded-lg flex items-center justify-center">
        <span className="text-gray-400">No hay media disponible</span>
      </div>
    )
  }

  const progress =
    mediaItems.length > 0 ? ((selectedIndex + 1) / mediaItems.length) * 100 : 0

  const renderVideo = (item: Extract<MediaItem, { type: "video" }>) => (
    <video
      autoPlay
      muted
      loop
      controlsList="nodownload"
      playsInline
      preload="metadata"
      className="video-item absolute inset-0 h-full w-full object-cover"
      poster={item.poster}
      src={item.url}
      style={{}}
    >
      <source src={item.url} type="video/mp4" />
    </video>
  )

  const getMediaKey = (item: MediaItem, index: number) => {
    const keyed = item as { key?: string; id?: string }
    return keyed.key || keyed.id || `${item.type}-${index}`
  }

  const markMediaUnavailable = (url: string) => {
    setFailedMediaUrls((current) => {
      if (current.has(url)) return current
      const next = new Set(current)
      next.add(url)
      return next
    })
  }

  const renderMissingImage = () => (
    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-400">
      <PlaceholderImage size={32} />
    </div>
  )

  return (
    <div className="flex flex-col gap-2 md:gap-4">
      {/* Mobile Slider */}
      <div className="md:hidden">
        <div className="relative">
          <div
            ref={mobileGalleryRef}
            className="flex snap-x snap-mandatory overflow-x-auto no-scrollbar"
          >
            {mediaItems.map((item, index) => (
              <div
                key={getMediaKey(item, index)}
                className="product-gallery-mobile-item relative min-w-full snap-center aspect-[3/4] bg-gray-50"
              >
                {item.type === "image" &&
                  item.url &&
                  !failedMediaUrls.has(item.url) && (
                  <Image
                    src={item.url}
                    alt={`Product image ${index + 1}`}
                    fill
                    className="object-cover"
                    quality={PRODUCT_GALLERY_MAIN_IMAGE_QUALITY}
                    sizes={PRODUCT_GALLERY_MOBILE_IMAGE_SIZES}
                    preload={index === 0}
                    onError={() => markMediaUnavailable(item.url)}
                  />
                )}
                {item.type === "image" && failedMediaUrls.has(item.url) &&
                  renderMissingImage()}
                {item.type === "video" && renderVideo(item)}

                {renderBadges()}
                {showVirtualTryOn && index === selectedIndex && (
                  <div className="absolute bottom-4 left-4">
                    <button
                      type="button"
                      onClick={() => onVirtualTryOn?.()}
                      className="flex w-fit items-center gap-2 bg-white/90 backdrop-blur-sm border border-black/10 px-5 py-2.5 text-[11px] font-lealti uppercase tracking-[0.15em] text-black transition hover:bg-white"
                    >
                      <PiPantsThin className="h-4 w-4" />
                      Probador Virtual
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-0 md:space-y-2">
            <div className="relative h-[3px] w-full overflow-hidden bg-black/10">
              <div
                className="absolute left-0 top-0 h-full bg-black transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-xs tracking-wide text-right text-black">
              {selectedIndex + 1}/{mediaItems.length}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Gallery */}
      <div className="hidden md:flex md:flex-col md:gap-4">
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-gray-50 h-full min-h-[500px]">
          {mediaItems[selectedIndex]?.type === "image" &&
            mediaItems[selectedIndex].url &&
            !failedMediaUrls.has(mediaItems[selectedIndex].url) && (
              <Image
                src={mediaItems[selectedIndex].url || ""}
                preload
                className="object-cover"
                alt={`Product image ${selectedIndex + 1}`}
                fill
                quality={PRODUCT_GALLERY_MAIN_IMAGE_QUALITY}
                sizes={PRODUCT_GALLERY_DESKTOP_MAIN_IMAGE_SIZES}
                onError={() =>
                  markMediaUnavailable(mediaItems[selectedIndex].url)
                }
              />
            )}
          {mediaItems[selectedIndex]?.type === "image" &&
            failedMediaUrls.has(mediaItems[selectedIndex].url) &&
            renderMissingImage()}
          {mediaItems[selectedIndex]?.type === "video" &&
            renderVideo(
              mediaItems[selectedIndex] as Extract<MediaItem, { type: "video" }>
            )}

          {renderBadges()}
          {showVirtualTryOn && (
            <div className="pointer-events-none absolute bottom-5 left-5">
              <button
                type="button"
                onClick={() => onVirtualTryOn?.()}
                className="pointer-events-auto flex w-fit items-center gap-2 bg-white/90 backdrop-blur-sm border border-black/10 px-5 py-2.5 text-[11px] font-lealti uppercase tracking-[0.15em] text-black transition hover:bg-white"
              >
                <PiPantsThin className="h-5 w-5" />
                Probador Virtual
              </button>
            </div>
          )}
        </div>

        {mediaItems.length > 1 && (
          <div className="flex gap-2">
            {mediaItems.map((item, index) => (
              <button
                key={getMediaKey(item, index)}
                onClick={() => setSelectedIndex(index)}
                className={`relative h-24 w-24 overflow-hidden border transition-all ${
                  selectedIndex === index ? "border-black" : "border-gray-200"
                }`}
              >
                {item.type === "image" &&
                  item.url &&
                  !failedMediaUrls.has(item.url) && (
                  <Image
                    src={item.url}
                    alt={`Product thumbnail ${index + 1}`}
                    fill
                    className="object-cover"
                    quality={PRODUCT_GALLERY_THUMB_IMAGE_QUALITY}
                    sizes={SQUARE_THUMB_IMAGE_SIZES}
                    onError={() => markMediaUnavailable(item.url)}
                  />
                )}
                {item.type === "image" && failedMediaUrls.has(item.url) &&
                  renderMissingImage()}
                {item.type === "video" && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black text-[10px] uppercase tracking-wide text-white">
                    Video
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ImageGallery
